import { LayoutDashboard, BookOpen, ChartLine, Settings, ShieldCheck, Users, Megaphone, ChartPie, ChevronLeft, ChevronRight, ChevronDown, ClipboardCheck, Building2, Radar, Zap, Network, ScanEye, Monitor, FileText } from "lucide-react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuItem, SidebarMenuButton, SidebarFooter, SidebarHeader, useSidebar } from "@/components/ui/sidebar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import lurneaxLogo from "@/assets/lurneax-logo.svg";
import { useRole } from "@/contexts/RoleContext";
import { SidebarUserMenu } from "./SidebarUserMenu";
import { usePathway } from "@/contexts/PathwayContext";
import { Button } from "@/components/ui/button";
import { useProjectName } from "@/hooks/use-project-name";
import { useState } from "react";
import { toast } from "sonner";
import { preloadPath } from "@/lib/routePreload";

const intentHandlers = (path: string) => {
  const warm = () => preloadPath(path);
  return { onPointerEnter: warm, onFocus: warm, onTouchStart: warm };
};

// Routes a Franchisee Prospect is allowed to actually open. Everything else
// in the sidebar is rendered but intercepted with a "Contact admin" toast.
const PROSPECT_ALLOWED_ROUTES = new Set<string>([
  "/learning/courses",
  "/learning/dashboard",
]);

interface LearningItem {
  title: string;
  url: string;
  icon: typeof LayoutDashboard;
  allowedRoles: string[];
  children?: LearningItem[];
}

const networkItems: LearningItem[] = [{
  title: "National Overview",
  url: "/national-dashboard",
  icon: Monitor,
  allowedRoles: ["master_admin", "admin", "state_partner", "growth_partner"]
}];

const learningItems: LearningItem[] = [{
  title: "Learning Dashboard",
  url: "/learning/dashboard",
  icon: LayoutDashboard,
  allowedRoles: ["master_admin", "admin", "state_partner", "growth_partner", "center_manager", "tutor", "franchisee_prospect"]
}, {
  title: "Course Catalogue",
  url: "/learning/courses",
  icon: BookOpen,
  allowedRoles: ["master_admin", "admin"],
  children: [{
    title: "Microlearning",
    url: "/learning/microlearning",
    icon: Zap,
    allowedRoles: ["master_admin", "admin"]
  }, {
    title: "Competency Mapping",
    url: "/learning/competency",
    icon: Radar,
    allowedRoles: ["master_admin", "admin"]
  }]
}, {
  title: "Tutor Audit",
  url: "/learning/tutor-audit",
  icon: ScanEye,
  allowedRoles: ["master_admin", "admin", "state_partner", "growth_partner", "center_manager", "tutor", "franchisee_prospect"]
}, {
  title: "Downloadable Resources",
  url: "/resources/downloads",
  icon: FileText,
  allowedRoles: ["master_admin", "admin", "state_partner", "growth_partner", "center_manager", "tutor", "franchisee_prospect"]
}];
const adminItems = [{
  title: "Competency Mapping",
  url: "/learning/competency",
  icon: Radar,
  allowedRoles: ["master_admin", "admin"]
}, {
  title: "Admin Settings",
  url: "/learning/admin-settings",
  icon: Settings,
  allowedRoles: ["master_admin", "admin"]
}, {
  title: "Audit Logs",
  url: "/learning/access-audit-logs",
  icon: ShieldCheck,
  allowedRoles: ["master_admin", "admin"]
}, {
  title: "Users",
  url: "/users",
  icon: Users,
  allowedRoles: ["master_admin", "admin", "center_manager", "franchisee_prospect"]
}, {
  title: "Centres",
  url: "/locations",
  icon: Building2,
  allowedRoles: ["master_admin", "admin", "franchisee_prospect"]
}, {
  title: "Communication",
  url: "/communication",
  icon: Megaphone,
  allowedRoles: ["master_admin", "admin", "franchisee_prospect"]
}, {
  title: "Reports",
  url: "/reports",
  icon: ChartPie,
  allowedRoles: ["master_admin", "admin", "center_manager", "franchisee_prospect"]
}];
export function LearningSidebar() {
  const {
    state,
    toggleSidebar
  } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const isActive = (url: string) => currentPath.startsWith(url);
  const {
    userRole
  } = useRole();
  const {
    setCurrentPathway
  } = usePathway();
  const { data: projectName } = useProjectName();
  const handleLogoClick = () => {
    navigate('/learning/dashboard');
  };
  const handleSwitchToAssessment = () => {
    setCurrentPathway("assessment");
    navigate('/assessment/dashboard');
  };
  const filteredNetworkItems = networkItems.filter(item => !!userRole && item.allowedRoles.includes(userRole));

  const [courseCatalogueOpen, setCourseCatalogueOpen] = useState(false);

  const isProspect = userRole === "franchisee_prospect";
  const handleProspectClick = (url: string) => (e: React.MouseEvent) => {
    if (isProspect && !PROSPECT_ALLOWED_ROUTES.has(url)) {
      e.preventDefault();
      toast.info("Contact your administrator for access to this section.");
    }
  };

  const filteredLearningItems = learningItems
    .filter(item => !!userRole && item.allowedRoles.includes(userRole))
    .map(item => ({
      ...item,
      children: item.children?.filter(child => !!userRole && child.allowedRoles.includes(userRole))
    }));
  return <TooltipProvider>
      <div className="relative group/sidebar">
        <Sidebar data-tour="sidebar" className={`${collapsed ? "w-14" : "w-64"} transition-all duration-300 ease-in-out`} collapsible="icon">
          <SidebarHeader className="p-3">
            <div className="flex flex-col items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                   <img loading="lazy" src={lurneaxLogo} alt="Lurneax" onClick={handleLogoClick} className="h-40 w-40 transition-all duration-300 hover:scale-105 cursor-pointer flex-shrink-0" />
                 </TooltipTrigger>
                 {collapsed && <TooltipContent side="right">Go to Dashboard</TooltipContent>}
               </Tooltip>
              {!collapsed && projectName && (
                <p className="text-xs font-semibold text-muted-foreground tracking-wide uppercase text-center">
                  {projectName}
                </p>
              )}
              
              {!isProspect && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size={collapsed ? "icon" : "sm"} onClick={handleSwitchToAssessment} className="w-full gap-2 border-border/50 hover:border-primary/50 hover:bg-primary/5">
                      <ClipboardCheck className="h-4 w-4 text-amber-500 flex-shrink-0" />
                      {!collapsed && <span className="text-xs">Switch to Assessments</span>}
                    </Button>
                  </TooltipTrigger>
                  {collapsed && <TooltipContent side="right">Switch to Assessments</TooltipContent>}
                </Tooltip>
              )}
            </div>
          </SidebarHeader>

          <SidebarContent>
            {filteredNetworkItems.length > 0 && (
              <SidebarGroup>
                <SidebarGroupLabel className={`transition-opacity duration-200 ${collapsed ? 'opacity-0' : 'opacity-100'}`}>
                  Network
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {filteredNetworkItems.map(item => (
                      <SidebarMenuItem key={item.title}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <SidebarMenuButton asChild isActive={isActive(item.url)}>
                              <Link to={item.url} onClick={handleProspectClick(item.url)} {...intentHandlers(item.url)} className="transition-all duration-200">
                                <item.icon className="h-4 w-4 flex-shrink-0" />
                                <span className={`transition-all duration-200 ${collapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100 w-auto'}`}>
                                  {item.title}
                                </span>
                              </Link>
                            </SidebarMenuButton>
                          </TooltipTrigger>
                          {collapsed && <TooltipContent side="right" className="font-medium bg-popover border-border shadow-lg">
                            <p>{item.title}</p>
                          </TooltipContent>}
                        </Tooltip>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}
            <SidebarGroup>
              <SidebarGroupLabel className={`transition-opacity duration-200 ${collapsed ? 'opacity-0' : 'opacity-100'}`}>
                Learning
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filteredLearningItems.map(item => (
                    <SidebarMenuItem key={item.title}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SidebarMenuButton asChild isActive={isActive(item.url)}>
                            <Link to={item.url} onClick={handleProspectClick(item.url)} {...intentHandlers(item.url)} className="transition-all duration-200" data-tour={`nav-${item.title.toLowerCase().replace(/\s+/g, '-')}`}>
                              <item.icon className="h-4 w-4 flex-shrink-0" />
                              <span className={`transition-all duration-200 ${collapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100 w-auto'}`}>
                                {item.title}
                              </span>
                              {item.children && item.children.length > 0 && !collapsed && (
                                <ChevronDown
                                  className={`ml-auto h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 ${courseCatalogueOpen ? 'rotate-180' : ''}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setCourseCatalogueOpen(!courseCatalogueOpen);
                                  }}
                                />
                              )}
                            </Link>
                          </SidebarMenuButton>
                        </TooltipTrigger>
                        {collapsed && <TooltipContent side="right" className="font-medium bg-popover border-border shadow-lg">
                            <p>{item.title}</p>
                          </TooltipContent>}
                      </Tooltip>
                      {/* Collapsible children */}
                      {item.children && item.children.length > 0 && !collapsed && courseCatalogueOpen && (
                        <SidebarMenu className="ml-4 mt-1 border-l border-border/50 pl-2">
                          {item.children.map(child => (
                            <SidebarMenuItem key={child.title}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <SidebarMenuButton asChild isActive={isActive(child.url)} className="text-sm">
                                    <Link to={child.url} onClick={handleProspectClick(child.url)} {...intentHandlers(child.url)} className="transition-all duration-200">
                                      <child.icon className="h-3.5 w-3.5 flex-shrink-0" />
                                      <span>{child.title}</span>
                                    </Link>
                                  </SidebarMenuButton>
                                </TooltipTrigger>
                              </Tooltip>
                            </SidebarMenuItem>
                          ))}
                        </SidebarMenu>
                      )}
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          <SidebarFooter className="border-t border-border">
            <SidebarUserMenu adminItems={adminItems} />
          </SidebarFooter>
        </Sidebar>

        {/* Hover-activated collapse button on sidebar edge */}
        <button onClick={toggleSidebar} className="hidden md:flex absolute top-1/2 -translate-y-1/2 -right-3 z-50 h-6 w-6 rounded-full bg-background border border-border shadow-md items-center justify-center opacity-0 group-hover/sidebar:opacity-100 transition-all duration-200 hover:bg-muted hover:scale-110">
          {collapsed ? <ChevronRight className="h-3 w-3 text-muted-foreground" /> : <ChevronLeft className="h-3 w-3 text-muted-foreground" />}
        </button>
      </div>
    </TooltipProvider>;
}